import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, Plus, Archive, Search, Hash } from 'lucide-react';
import { useChAuth } from '../contexts/ChAuthContext';
import { chProjectService } from '../services';
import { isChStaffRole, type ChProject } from '../types';

export function ChProjectsList() {
  const { user } = useChAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ChProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    try {
      setLoading(true);
      const data = await chProjectService.getAll();
      setProjects(data);
    } catch {
      // handle silently
    } finally {
      setLoading(false);
    }
  }

  const filtered = projects.filter(p => {
    if (!showArchived && p.archived) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q) ||
        p.workcode?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto ch-animate-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="ch-heading ch-heading-lg">Projetos</h1>
        {isChStaffRole(user?.role) && (
          <button
            onClick={() => navigate('/contenthub/projetos/novo')}
            className="ch-btn ch-btn-primary"
          >
            <Plus className="w-4 h-4" />
            Novo projeto
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            className="ch-input pl-10"
            placeholder="Pesquisar projetos..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button
          onClick={() => setShowArchived(!showArchived)}
          className={`ch-btn ${showArchived ? 'ch-btn-primary' : 'ch-btn-ghost'} ch-btn-sm`}
        >
          <Archive className="w-3.5 h-3.5" />
          Arquivados
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="ch-card animate-pulse flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gray-100" />
              <div className="flex-1">
                <div className="h-4 w-40 bg-gray-100 rounded mb-2" />
                <div className="h-3 w-24 bg-gray-50 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="ch-empty">
          <FolderKanban className="ch-empty-icon" />
          <p className="text-gray-500 font-medium">Nenhum projeto encontrado</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(project => (
            <button
              key={project.id}
              onClick={() => navigate(`/contenthub/projetos/${project.slug}`)}
              className="ch-card w-full text-left flex items-center gap-4 hover:shadow-md transition-shadow group"
            >
              <div className="w-10 h-10 rounded-xl bg-[var(--ch-ink)] flex items-center justify-center flex-shrink-0">
                <FolderKanban className="w-5 h-5 text-[var(--ch-lime)]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-[var(--ch-ink)] text-sm truncate group-hover:text-[var(--ch-blue)] transition-colors">
                  {project.name}
                </h3>
                <p className="text-xs text-gray-400 truncate">/{project.slug}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {project.workcode && (
                  <span className="ch-chip">
                    <Hash className="w-3 h-3" />
                    {project.workcode}
                  </span>
                )}
                {project.archived && (
                  <span className="ch-chip text-orange-600 bg-orange-50">
                    <Archive className="w-3 h-3" />
                    Arquivado
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
